import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronRight, SendHorizontal, Sparkles } from "lucide-react";
import { ask, getSamplePrompts, streamNarration } from "../lib/api";
import { EvidencePanel } from "../components/EvidencePanel";
import { ResultNumbers } from "../components/ResultNumbers";
import { DecisionCard } from "../components/DecisionCard";
import { PageHeader } from "../components/Shell";

const START_GROUPS = [["board_note", 0], ["reallocation", 5], ["partner_accountability", 2],
                      ["peer_mca_basis", 0], ["state_district_briefings", 0], ["aspirational_districts", 0]];


export default function AskPage() {
  const sessionId = useMemo(() => `s-${Math.random().toString(36).slice(2, 10)}`, []);
  const [prompts, setPrompts] = useState(null);
  const [question, setQuestion] = useState("");
  const [turns, setTurns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(null);
  const [pending, setPending] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [showAll, setShowAll] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    getSamplePrompts().then(setPrompts).catch(() => {});
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [turns, busy]);

  const submit = async (q, intent) => {
    const text = (q ?? question).trim();
    if (!text || busy) return;
    setQuestion("");
    setBusy(true);
    setPending(true);
    const idx = turns.length;
    setTurns((t) => [...t, { question: text, answer: null, narrative: "", error: null }]);
    try {
      const answer = await ask(text, sessionId, intent);
      setTurns((t) => t.map((x, i) => (i === idx ? { ...x, answer } : x)));
      setActive(answer);
      setPending(false);
      streamNarration(
        answer.answer_id,
        (chunk) =>
          setTurns((t) => t.map((x, i) => (i === idx ? { ...x, narrative: x.narrative + chunk } : x))),
        () => setBusy(false),
        () => setBusy(false)
      );
    } catch (e) {
      setTurns((t) => t.map((x, i) => (i === idx ? { ...x, error: e.message } : x)));
      setBusy(false);
      setPending(false);
    }
  };

  return (
    <div className={`grid gap-10 items-start ${
      turns.length === 0
        ? "grid-cols-1 max-w-3xl"
        : "grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]"
    }`}>
      <div className="min-w-0">
        <PageHeader title="What can I help you with?" testId="ask-header" />

        <div className="space-y-4" data-testid="ask-thread">
          {turns.map((t, i) => (
            <div key={i} className="space-y-3">
              <div className="flex justify-end">
                <div className="bg-[#760D28] text-white rounded-2xl rounded-br-md px-4 py-2.5 max-w-[85%] text-[14px] leading-relaxed"
                     data-testid={`ask-question-${i}`}>
                  {t.question}
                </div>
              </div>

              {t.error && (
                <div className="border border-rose-200 bg-rose-50 rounded-lg p-3 text-xs text-rose-900"
                     data-testid={`ask-error-${i}`}>
                  {t.error}
                </div>
              )}

              {t.answer && (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setActive(t.answer)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setActive(t.answer);
                    }
                  }}
                  data-testid={`ask-answer-${i}`}
                  className={`w-full text-left bg-white border rounded-2xl p-5 space-y-3.5 cursor-pointer transition-colors focus-visible:ring-2 focus-visible:ring-[#A01236] outline-none ${
                    active?.answer_id === t.answer.answer_id
                      ? "border-[#A01236]/50"
                      : "border-stone-200 hover:border-stone-300"
                  }`}
                >
                  <DecisionCard
                    decision={t.answer.decision}
                    answerability={t.answer.answerability}
                    narrative={t.answer.kind === "refusal" ? undefined : t.narrative}
                    printState={t.answer.kind === "state_brief_multi" && t.answer.result?.states?.length === 1
                      ? t.answer.result.states[0] : null}
                    printBoard={t.answer.kind === "board_note"}
                    testId={`ask-decision-${i}`}
                  />

                  <div className="text-[12px] text-[#A01236] font-medium">
                    View the evidence for this answer →
                  </div>
                </div>
              )}
            </div>
          ))}
          <div ref={endRef} />
        </div>

        <div className="mt-6 mb-8">
          <div className="bg-white border border-stone-200 rounded-2xl p-2.5 flex items-end gap-2 focus-within:border-[#A01236]/50 transition-colors">
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  submit();
                }
              }}
              rows={2}
              placeholder="e.g. Which districts are absorbing the least of the money we have released?"
              data-testid="ask-input"
              className="flex-1 resize-none bg-transparent px-3 py-2 text-[14px] outline-none placeholder:text-stone-600"
            />
            <button
              onClick={() => submit()}
              disabled={busy || !question.trim()}
              data-testid="ask-submit-button"
              className="shrink-0 bg-[#A01236] text-white rounded-xl px-4 py-3 disabled:opacity-30 hover:bg-[#760D28] transition-colors"
            >
              <SendHorizontal className="w-4 h-4" />
            </button>
          </div>
        </div>

        {prompts && turns.length === 0 && (
          <div className="space-y-6">
            <div data-testid="start-here">
              <div className="text-[12px] font-semibold text-stone-600 mb-2.5">Start with one of these</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {START_GROUPS.map(([id, idx]) => {
                  const g = prompts.groups.find((x) => x.id === id);
                  return g && g.prompts[idx] ? { ...g, pick: g.prompts[idx] } : null;
                })
                  .filter(Boolean)
                  .map((g) => (
                    <button
                      key={g.id}
                      onClick={() => submit(g.pick.q, g.pick.intent)}
                      data-testid={`start-prompt-${g.id}`}
                      className="text-left bg-white border border-stone-200 hover:border-[#A01236]/50 rounded-xl px-4 py-3 transition-colors group"
                    >
                      <span className="block text-[12px] font-semibold text-[#A01236]">{g.title}</span>
                      <span className="block text-[14px] text-stone-800 mt-0.5 leading-snug group-hover:text-stone-900">
                        {g.pick.q}
                      </span>
                    </button>
                  ))}
              </div>
            </div>

            <button
              onClick={() => setShowAll((v) => !v)}
              data-testid="browse-all-prompts"
              className="text-[13px] font-medium text-stone-600 hover:text-[#A01236] inline-flex items-center gap-1.5 transition-colors"
            >
              <ChevronRight className={`w-4 h-4 transition-transform ${showAll ? "rotate-90" : ""}`} />
              {showAll ? "Hide" : "Browse"} all {prompts.groups.reduce((n, g) => n + g.prompts.length, 0)} questions
            </button>

            {showAll && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 auto-rows-min"
               data-testid="sample-prompts">
            {prompts.groups.map((g) => {
              const open = expanded[g.id];
              return (
                <div key={g.id}
                     className={`border rounded-xl bg-white transition-colors ${
                       open ? "border-[#A01236]/40" : "border-stone-200 hover:border-stone-300"
                     }`}
                     data-testid={`prompt-group-${g.id}`}>
                  <button
                    onClick={() => setExpanded((e) => ({ ...e, [g.id]: !e[g.id] }))}
                    data-testid={`prompt-accordion-${g.id}`}
                    aria-expanded={open ? "true" : "false"}
                    className="w-full flex items-start gap-3 px-4 py-3.5 text-left group"
                  >
                    <ChevronRight
                      className={`w-4 h-4 mt-0.5 text-stone-600 shrink-0 transition-transform duration-200 ${
                        open ? "rotate-90 text-[#A01236]" : "group-hover:text-stone-800"
                      }`}
                    />
                    <span className="min-w-0">
                      <span className="block font-display text-[14px] font-semibold text-stone-900">
                        {g.title}
                      </span>
                    </span>
                    <span className="ml-auto text-[12px] text-stone-600 shrink-0 tabular-nums">
                      {g.prompts.length}
                    </span>
                  </button>
                  {open && (
                    <div className="px-4 pb-4 pl-11 flex flex-col gap-2.5">
                      {g.prompts.map((p, i) => (
                        <button
                          key={i}
                          onClick={() => submit(p.q, p.intent)}
                          data-testid={`prompt-chip-${g.id}-${i}`}
                          className="text-left text-[14px] text-stone-700 hover:text-[#A01236] transition-colors leading-snug"
                        >
                          {p.q}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
            )}
          </div>
        )}
      </div>

      {turns.length > 0 && (
        <div className={`xl:sticky xl:top-[76px] min-w-0 flex flex-col gap-2 ${
          active ? "h-[calc(100vh-120px)]" : ""
        }`}>
          {pending && (
            <div className="shrink-0 flex items-center gap-2 rounded-xl border border-[#A01236]/30 bg-[#FDF2F4] px-4 py-2.5 text-[13px] text-[#760D28]"
                 data-testid="ask-evidence-pending">
              <Sparkles className="w-3.5 h-3.5 animate-pulse" />
              Computing the next answer — the evidence below is still the previous one.
            </div>
          )}
          {active && (
            /* Keyed on the answer, so a new answer gets a fresh panel: tab back to Numbers,
               scroll back to the top, table sorts reset. Without this the panel kept the
               previous answer's scroll position and read as frozen. */
            <div className="flex-1 min-h-0">
              <EvidencePanel key={active.answer_id} result={active.result}
                             testId="ask-evidence-panel">
                <ResultNumbers key={active.answer_id} result={active.result} />
              </EvidencePanel>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

